import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'
import HomeView from '../views/HomeView.vue'
import LoginView from '../components/Login.vue'
import DashboardView from '../components/Dashboard.vue'
import ProfileView from '../views/ProfileView.vue'
import BoardView from '../views/BoardView.vue'
import BoardsView from '../views/BoardsView.vue'
import CreateBoardView from '../views/CreateBoardView.vue'
import SettingsView from '../views/SettingsView.vue'
import ResetPasswordView from '../views/ResetPasswordView.vue'
import MyTasksView from '../views/MyTasksView.vue'
import ProductionShell from '../views/production/ProductionShell.vue'
import ProductionOverviewView from '../views/production/ProductionOverviewView.vue'
import ProductionProjectsView from '../views/production/ProductionProjectsView.vue'
import ProductionProjectDetailView from '../views/production/ProductionProjectDetailView.vue'
import ProductionProductionsView from '../views/production/ProductionProductionsView.vue'
import ProductionTeamView from '../views/production/ProductionTeamView.vue'
import ProductionDetailLayout from '../views/production/ProductionDetailLayout.vue'
import ProductionDetailOverviewView from '../views/production/ProductionDetailOverviewView.vue'
import ProductionPeopleView from '../views/production/ProductionPeopleView.vue'
import ProductionScriptView from '../views/production/ProductionScriptView.vue'
import ProductionShootView from '../views/production/ProductionShootView.vue'
import ProductionPlatformsView from '../views/production/ProductionPlatformsView.vue'
import ProductionEditingView from '../views/production/ProductionEditingView.vue'
import ProductionCommentsView from '../views/production/ProductionCommentsView.vue'
import ProductionScenesView from '../views/production/ProductionScenesView.vue'
import ProductionReleaseView from '../views/production/ProductionReleaseView.vue'
import ProductionCompletedView from '../views/production/ProductionCompletedView.vue'
import ProductionAnalyticsView from '../views/production/ProductionAnalyticsView.vue'

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL || '/'),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: ResetPasswordView
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: DashboardView,
      meta: { requiresAuth: true, title: 'Dashboard' }
    },
    {
      path: '/profile',
      name: 'profile',
      component: ProfileView,
      meta: { requiresAuth: true, title: 'Profile' }
    },
    {
      path: '/boards',
      name: 'boards',
      component: BoardsView,
      meta: { requiresAuth: true, title: 'Boards' }
    },
    {
      path: '/boards/create',
      name: 'create-board',
      component: CreateBoardView,
      meta: { requiresAuth: true, title: 'Create Board' }
    },
    {
      path: '/board/:boardId',
      name: 'board',
      component: BoardView,
      meta: { requiresAuth: true, title: 'Board' }
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('../views/UsersView.vue'),
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        title: 'Users'
      }
    },
    {
      path: '/settings',
      name: 'settings',
      component: SettingsView,
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        title: 'Settings'
      }
    },
    {
      path: '/my-tasks',
      name: 'my-tasks',
      component: MyTasksView,
      meta: { requiresAuth: true, title: 'My Tasks' }
    },
    {
      path: '/production',
      component: ProductionShell,
      meta: { requiresAuth: true, requiresProduction: true, title: 'Production' },
      children: [
        { path: '', name: 'ProductionOverview', component: ProductionOverviewView, meta: { title: 'Production' } },
        { path: 'projects', name: 'ProductionProjects', component: ProductionProjectsView, meta: { title: 'Projects' } },
        { path: 'projects/:projectId', name: 'ProductionProjectDetail', component: ProductionProjectDetailView, meta: { title: 'Project' }, props: true },
        { path: 'productions', name: 'ProductionProductions', component: ProductionProductionsView, meta: { title: 'Productions' } },
        { path: 'team', name: 'ProductionTeam', component: ProductionTeamView, meta: { title: 'Team', productionAdminOnly: true } },
        {
          path: 'productions/:productionId',
          component: ProductionDetailLayout,
          props: true,
          meta: { title: 'Production' },
          children: [
            { path: '', name: 'ProductionDetailOverview', component: ProductionDetailOverviewView, meta: { title: 'Production' } },
            { path: 'people', name: 'ProductionDetailPeople', component: ProductionPeopleView, meta: { title: 'People' } },
            { path: 'script', name: 'ProductionDetailScript', component: ProductionScriptView, meta: { title: 'Script' } },
            { path: 'scenes', name: 'ProductionDetailScenes', component: ProductionScenesView, meta: { title: 'Scenes' } },
            { path: 'shoot', name: 'ProductionDetailShoot', component: ProductionShootView, meta: { title: 'Shoot' } },
            { path: 'platforms', name: 'ProductionDetailPlatforms', component: ProductionPlatformsView, meta: { title: 'Platforms' } },
            { path: 'editing', name: 'ProductionDetailEditing', component: ProductionEditingView, meta: { title: 'Editing' } },
            { path: 'comments', name: 'ProductionDetailComments', component: ProductionCommentsView, meta: { title: 'Comments' } },
            { path: 'release', name: 'ProductionDetailRelease', component: ProductionReleaseView, meta: { title: 'Ready for release' } },
            { path: 'completed', name: 'ProductionDetailCompleted', component: ProductionCompletedView, meta: { title: 'Completed' } },
            { path: 'analytics', name: 'ProductionDetailAnalytics', component: ProductionAnalyticsView, meta: { title: 'Analytics' } }
          ]
        }
      ]
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('../views/NotFound.vue')
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  document.title = to.meta.title ? `${to.meta.title} - Tiye Move` : 'Tiye Move'

  if (!store.state.auth.initialized) {
    await store.dispatch('auth/fetchSession')
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin)
  const requiresProduction = to.matched.some(record => record.meta.requiresProduction)
  const productionAdminOnly = to.matched.some(record => record.meta.productionAdminOnly)
  const isAuthenticated = store.getters['auth/isAuthenticated']

  if (requiresAuth && !isAuthenticated) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  if (requiresAdmin && !store.getters['auth/isMoveAdmin']) {
    next({ path: '/dashboard', query: { error: 'insufficient_permissions' } })
    return
  }

  if (requiresProduction && !store.getters['auth/hasProductionAccess']) {
    next({ path: '/dashboard', query: { error: 'insufficient_permissions' } })
    return
  }

  if (productionAdminOnly && !store.getters['auth/isProductionAdmin']) {
    next({ path: '/production' })
    return
  }

  if (to.name === 'login' && isAuthenticated) {
    next('/dashboard')
    return
  }

  next()
})

export default router
